import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { LuEyeOff, LuHash, LuImage, LuImageOff, LuImages } from 'react-icons/lu';
import { API_BASE_URL } from '../../config';
import { cx } from '../../utils/cx.js';
import { channelLabel } from '../../utils/sources.js';
import { Card, CardHeader } from '../ui/Card';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import SegmentedControl from '../ui/SegmentedControl';
import { SpinnerRing } from '../ui/Spinner';
import Alert from '../ui/Alert';
import GalleryLightbox from './GalleryLightbox';
import { collectPictures, hiddenPictureIds } from './dumpData.js';

const ALL = '__all__';
const CHIPS_FOLDED = 12;

const Thumbnail = ({ picture, onOpen, isAdminUser }) => {
  const [state, setState] = useState('loading');
  const label = `Voir la photo${picture.channel ? ` de #${channelLabel(picture.channel)}` : ''}${isAdminUser && picture.username ? ` par ${picture.username}` : ''}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      className={cx(
        'group relative aspect-square cursor-zoom-in overflow-hidden rounded-xl bg-gray-100 outline-none transition-shadow hover:shadow-md dark:bg-night-900',
        'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-night-800',
        picture.isHidden && 'ring-2 ring-red-400/70',
      )}
    >
      {state !== 'ready' && (
        <span className="absolute inset-0 flex items-center justify-center text-gray-300 dark:text-gray-600" aria-hidden>
          {state === 'error' ? <LuImageOff className="h-6 w-6" /> : <LuImage className="h-6 w-6 animate-pulse" />}
        </span>
      )}
      {state !== 'error' && (
        <img
          src={picture.blob_url}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setState('ready')}
          onError={() => setState('error')}
          className={cx(
            'h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]',
            state === 'ready' ? (picture.isHidden ? 'opacity-50 grayscale' : 'opacity-100') : 'opacity-0',
          )}
        />
      )}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-6 text-left text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden>
        {isAdminUser && picture.username ? picture.username : picture.date?.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
      </span>
      {picture.isHidden && (
        <span className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow" title="Masquée pour le client">
          <LuEyeOff className="h-3.5 w-3.5" aria-hidden />
        </span>
      )}
    </button>
  );
};

/**
 * Photos of the week, grouped by channel. Filters: workspace (when several), channel chips,
 * and for admins the hidden-photo review. Hidden photos never reach clients.
 */
export const WeeklyGallery = memo(({ report, isAdminUser, dump = null, fetchWithAuth, clientSlug, weekId, onHiddenPicturesChange }) => {
  const [hiddenImages, setHiddenImages] = useState(() => hiddenPictureIds(report));
  const [workspace, setWorkspace] = useState(ALL);
  const [channel, setChannel] = useState(ALL);
  const [visibility, setVisibility] = useState('all');
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [toggleError, setToggleError] = useState(null);

  useEffect(() => {
    setHiddenImages(hiddenPictureIds(report));
  }, [report?.hidden_pictures]); // eslint-disable-line react-hooks/exhaustive-deps

  const hiddenSet = useMemo(() => new Set(hiddenImages), [hiddenImages]);
  const isHidden = useCallback((imgId) => hiddenSet.has(imgId), [hiddenSet]);

  // Clients never receive hidden photos; admins see them flagged.
  const pictures = useMemo(
    () => collectPictures(dump)
      .map((p) => ({ ...p, isHidden: hiddenSet.has(p.imgId) }))
      .filter((p) => isAdminUser || !p.isHidden),
    [dump, hiddenSet, isAdminUser],
  );

  const workspaces = useMemo(() => [...new Set(pictures.map((p) => p.workspace))], [pictures]);
  const inWorkspace = useMemo(
    () => (workspace === ALL ? pictures : pictures.filter((p) => p.workspace === workspace)),
    [pictures, workspace],
  );
  const channelCounts = useMemo(() => {
    const counts = new Map();
    inWorkspace.forEach((p) => counts.set(p.channel, (counts.get(p.channel) || 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [inWorkspace]);

  const visiblePictures = useMemo(() => inWorkspace.filter((p) => (
    (channel === ALL || p.channel === channel)
    && (visibility === 'all' || (visibility === 'hidden' ? p.isHidden : !p.isHidden))
  )), [inWorkspace, channel, visibility]);

  const groups = useMemo(() => {
    const byKey = new Map();
    visiblePictures.forEach((p, index) => {
      const key = `${p.workspace}/${p.channel}`;
      if (!byKey.has(key)) byKey.set(key, { key, workspace: p.workspace, channel: p.channel, items: [] });
      byKey.get(key).items.push({ picture: p, index });
    });
    return [...byKey.values()];
  }, [visiblePictures]);

  // A filter change can remove the channel being viewed: fall back to "all".
  useEffect(() => {
    if (channel !== ALL && !channelCounts.some(([c]) => c === channel)) setChannel(ALL);
  }, [channelCounts, channel]);

  // Un-hiding a photo while reviewing "Masquées" shortens the list under the viewer.
  useEffect(() => {
    if (lightboxIndex !== null && lightboxIndex >= visiblePictures.length) {
      setLightboxIndex(visiblePictures.length ? visiblePictures.length - 1 : null);
    }
  }, [lightboxIndex, visiblePictures.length]);

  const hiddenCount = pictures.filter((p) => p.isHidden).length;

  // The visibility filter disappears with the last hidden photo: never leave it stuck on "Masquées".
  useEffect(() => {
    if (hiddenCount === 0 && visibility !== 'all') setVisibility('all');
  }, [hiddenCount, visibility]);

  const toggleImageVisibility = async (imgId) => {
    const previous = hiddenImages;
    const next = previous.includes(imgId) ? previous.filter((i) => i !== imgId) : [...previous, imgId];
    setHiddenImages(next);
    setPendingId(imgId);
    setToggleError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/weekly/client/${clientSlug}/week/${weekId}/hidden-pictures`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden_pictures: next }),
      });
      if (!response.ok) throw new Error('Error updating hidden images');
      // The gallery unmounts with its tab: the report must hold the saved list.
      onHiddenPicturesChange?.(next);
    } catch (error) {
      console.error('Error updating visibility:', error);
      setHiddenImages(previous);
      setToggleError('La visibilité de la photo n\'a pas pu être enregistrée. Réessayez.');
    } finally {
      setPendingId(null);
    }
  };

  const header = (count) => (
    <CardHeader
      icon={LuImages}
      title="Galerie"
      subtitle="Photos partagées par les équipes terrain pendant la semaine"
      badge={count !== null && <Badge tone="violet" size="sm">{count.toLocaleString('fr-FR')} photo{count > 1 ? 's' : ''}</Badge>}
    />
  );

  if (report?.steps?.dump === 'failed') {
    return (
      <Card>
        {header(null)}
        <EmptyState icon={LuImageOff} tone="red" title="Récupération des données échouée" description="Les photos de cette semaine n'ont pas pu être récupérées." />
      </Card>
    );
  }

  if (!report?.steps || report.steps.dump !== 'completed') {
    return (
      <Card>
        {header(null)}
        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
          <SpinnerRing size="md" label="Récupération des données" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Les photos apparaîtront dès la fin de la récupération des données.</p>
        </div>
      </Card>
    );
  }

  if (pictures.length === 0) {
    const downloading = report.steps?.download?.status && report.steps.download.status !== 'completed';
    return (
      <Card>
        {header(0)}
        <EmptyState
          icon={LuImages}
          tone="gray"
          title={!dump?.data?.workspaces ? 'Aucune donnée pour ce rapport' : downloading ? 'Photos en cours de téléchargement' : 'Aucune photo cette semaine'}
          description={downloading ? 'Revenez dans quelques instants : la galerie se remplit au fil du téléchargement.' : 'Aucune photo n\'a été partagée sur la période.'}
        />
      </Card>
    );
  }

  const hasFilters = workspaces.length > 1 || (isAdminUser && hiddenCount > 0) || channelCounts.length > 1;
  const chips = [[ALL, inWorkspace.length], ...channelCounts];
  const foldable = chips.length > CHIPS_FOLDED + 1;
  const shownChips = foldable && !chipsExpanded ? chips.slice(0, CHIPS_FOLDED) : chips;

  return (
    <Card>
      {header(pictures.length)}

      {hasFilters && (
        <div className="space-y-3 border-b border-gray-100 px-4 py-3 dark:border-gray-900 sm:px-5">
          {(workspaces.length > 1 || (isAdminUser && hiddenCount > 0)) && (
            <div className="flex flex-wrap items-center gap-3">
              {workspaces.length > 1 && (
                <SegmentedControl
                  size="sm"
                  ariaLabel="Espace de travail"
                  value={workspace}
                  onChange={(v) => { setWorkspace(v); setChannel(ALL); }}
                  options={[{ value: ALL, label: 'Tous les espaces' }, ...workspaces.map((w) => ({ value: w, label: w }))]}
                />
              )}
              {isAdminUser && hiddenCount > 0 && (
                <SegmentedControl
                  size="sm"
                  ariaLabel="Visibilité"
                  value={visibility}
                  onChange={setVisibility}
                  options={[
                    { value: 'all', label: 'Toutes' },
                    { value: 'visible', label: 'Visibles' },
                    { value: 'hidden', label: 'Masquées', count: hiddenCount },
                  ]}
                />
              )}
            </div>
          )}

          {channelCounts.length > 1 && (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrer par canal">
              {shownChips.map(([key, count]) => {
                const active = channel === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setChannel(key)}
                    className={cx(
                      'inline-flex min-h-[32px] cursor-pointer items-center gap-1 rounded-full border px-3 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand-500',
                      active
                        ? 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-800 dark:bg-brand-900 dark:text-brand-300'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:border-gray-800 dark:bg-night-850 dark:text-gray-300 dark:hover:text-white',
                    )}
                  >
                    {key === ALL ? 'Tous les canaux' : <><LuHash className="h-3 w-3 opacity-60" aria-hidden />{channelLabel(key)}</>}
                    <span className="tabular-nums opacity-60">{count}</span>
                  </button>
                );
              })}
              {foldable && (
                <button
                  type="button"
                  onClick={() => setChipsExpanded((v) => !v)}
                  className="inline-flex min-h-[32px] cursor-pointer items-center rounded-full px-3 text-xs font-semibold text-brand-600 outline-none hover:bg-brand-50 focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400 dark:hover:bg-brand-950"
                >
                  {chipsExpanded ? 'Voir moins' : `+ ${chips.length - CHIPS_FOLDED} canaux`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-6 p-4 sm:p-5">
        {toggleError && <Alert tone="danger">{toggleError}</Alert>}
        {groups.length === 0 ? (
          <EmptyState icon={LuImages} tone="gray" title="Aucune photo pour ce filtre" description="Modifiez les filtres pour voir d'autres photos." />
        ) : groups.map((group) => {
          const groupHidden = group.items.filter(({ picture }) => picture.isHidden).length;
          return (
            <section key={group.key} aria-label={`Canal ${channelLabel(group.channel)}`}>
              <h3 className="sticky top-0 z-10 -mx-4 mb-3 flex items-center gap-2 bg-white/90 px-4 py-2 text-sm font-semibold text-gray-900 backdrop-blur dark:bg-night-800/90 dark:text-white sm:-mx-5 sm:px-5">
                <LuHash className="h-4 w-4 text-gray-400" aria-hidden />
                {channelLabel(group.channel)}
                {workspaces.length > 1 && <span className="text-xs font-normal text-gray-400">· {group.workspace}</span>}
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  {group.items.length} photo{group.items.length > 1 ? 's' : ''}
                  {isAdminUser && groupHidden > 0 && ` · ${groupHidden} masquée${groupHidden > 1 ? 's' : ''}`}
                </span>
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8">
                {group.items.map(({ picture, index }) => (
                  <Thumbnail key={picture.imgId} picture={picture} isAdminUser={isAdminUser} onOpen={() => setLightboxIndex(index)} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <GalleryLightbox
        pictures={visiblePictures}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        isAdminUser={isAdminUser}
        isHidden={isHidden}
        onToggleHidden={toggleImageVisibility}
        pendingId={pendingId}
      />
    </Card>
  );
});

WeeklyGallery.displayName = 'WeeklyGallery';

export default WeeklyGallery;
